// /abacus — the abacus itself, with no DOM: the three frames, the value on them, the
// step-by-step plan of an addition or a subtraction (the formulas of mental arithmetic),
// the example generator held to a set of formulas, the course. Pinned by
// tests/js/abacus-core.test.mjs. Rationale and sources — docs/why/abacus.md.
//
// A ROD holds {u, l}: u — upper beads at the beam (5 each), l — lower beads at the beam
// (1 each). The Russian schoty have no upper deck: l is the number of beads pushed to
// the LEFT on that wire. Rods are indexed from the LEFT (the highest place) — on the
// schoty from the TOP wire.

export const KINDS = {
    // 1 + 4: one heaven bead, four earth beads; a rod shows 0…9
    soroban: { up: 1, low: 4, five: true, rodMax: 9, rodsMin: 3, rodsMax: 17, rodsDefault: 13 },
    // 2 + 5: the extra beads let a rod hold up to 15 (merchants carried later); the
    // formulas use the soroban part of it — the same plan, the extra beads stay apart
    suanpan: { up: 2, low: 5, five: true, rodMax: 15, rodsMin: 3, rodsMax: 15, rodsDefault: 13 },
    // ten beads a wire, the 5th and 6th dark; counted by moving to the left
    schoty: { up: 0, low: 10, five: false, rodMax: 10, rodsMin: 3, rodsMax: 12, rodsDefault: 7 },
};

export const rodValue = (kind, r) => (KINDS[kind].five ? r.u * 5 : 0) + r.l;

/** The beads that show digit d in the usual (canonical) way. */
export function canon(kind, d) {
    if (!KINDS[kind].five) return { u: 0, l: d };
    return { u: d >= 5 ? 1 : 0, l: d % 5 };
}

export const emptyRods = (n) => Array.from({ length: n }, () => ({ u: 0, l: 0 }));

/** Σ rodValue × 10^place as an integer of the smallest place (rods may hold more than 9). */
export function scaledOf(kind, rods) {
    let t = 0;
    for (let i = 0; i < rods.length; i++) t = t * 10 + rodValue(kind, rods[i]);
    return t;
}

/** The capacity of n rods: the largest number they show in the canonical way. */
export const capacity = (n) => Math.pow(10, n) - 1;

/** Digits of a scaled value spread over n rods (left = highest); null if it does not fit. */
export function digitsOf(scaled, n) {
    if (!Number.isFinite(scaled) || scaled < 0 || scaled > capacity(n)) return null;
    const s = String(Math.round(scaled)).padStart(n, '0');
    return s.split('').map(Number);
}

export function rodsOf(kind, digits) { return digits.map((d) => canon(kind, d)); }

/** Is every rod in its canonical form (suanpan with 5 earth beads, schoty with all 10 — no)? */
export function isCanonical(kind, rods) {
    return rods.every((r) => {
        const v = rodValue(kind, r);
        if (v > 9) return false;
        const c = canon(kind, v);
        return c.u === r.u && c.l === r.l;
    });
}

/* ═══════════════════════ the plan: which beads move, by which formula ═══════════════════════

   Every step is ONE rod: du — upper beads (+ toward the beam), dl — lower beads (+ toward
   the beam / to the left on the schoty). The formula names the method by which the digit
   went in:
     direct  — «просто»: the beads are there
     brother — «помощь брата» (5): +4 = +5 − 1, −4 = −5 + 1
     friend  — «помощь друга» (10): +7 = +10 − 3, −7 = −10 + 3
     mix     — «друг и брат»: the friend's −3 (or +3) itself needs the brother:
               +7 = +10 − 5 + 2, −7 = −10 + 5 − 2
   A step of the carry (the friend's +10 on the rod to the left) or the borrow (−10) has the
   role 'carry' and its OWN formula — 99 + 1 is a friend that calls a friend.

   Order inside a friend (as the schools teach the movement, left hand free):
     addition    — first the complement off this rod, then +1 on the left one
     subtraction — first −1 on the left rod, then the complement onto this one */

export class PlanError extends Error {
    constructor(code) { super(code); this.code = code; }
}

function step(out, kind, rods, i, du, dl, formula, ctx) {
    const r = rods[i];
    r.u += du; r.l += dl;
    out.push({ rod: i, du, dl, formula, role: ctx.role, d: ctx.d, sign: ctx.sign, op: ctx.op, place: ctx.place, after: rodValue(kind, r) });
}

function addDigit(kind, rods, i, d, out, ctx) {
    if (i < 0) throw new PlanError('overflow');
    const r = rods[i];
    const c = rodValue(kind, r);
    const here = Object.assign({}, ctx, { d, sign: 1 });
    if (!KINDS[kind].five) {
        if (c + d <= 9) { step(out, kind, rods, i, 0, d, 'direct', here); return; }
        step(out, kind, rods, i, 0, -(10 - d), 'friend', here);
        addDigit(kind, rods, i - 1, 1, out, Object.assign({}, ctx, { role: 'carry' }));
        return;
    }
    const l = r.l;
    if (c + d <= 9) {
        if (d >= 5) step(out, kind, rods, i, 1, d - 5, 'direct', here);
        else if (l + d <= 4) step(out, kind, rods, i, 0, d, 'direct', here);
        else step(out, kind, rods, i, 1, -(5 - d), 'brother', here);
        return;
    }
    const k = 10 - d;
    if (k >= 5) step(out, kind, rods, i, -1, -(k - 5), 'friend', here);
    else if (l >= k) step(out, kind, rods, i, 0, -k, 'friend', here);
    else step(out, kind, rods, i, -1, 5 - k, 'mix', here);
    addDigit(kind, rods, i - 1, 1, out, Object.assign({}, ctx, { role: 'carry' }));
}

function subDigit(kind, rods, i, d, out, ctx) {
    if (i < 0) throw new PlanError('negative');
    const r = rods[i];
    const c = rodValue(kind, r);
    const here = Object.assign({}, ctx, { d, sign: -1 });
    if (!KINDS[kind].five) {
        if (c >= d) { step(out, kind, rods, i, 0, -d, 'direct', here); return; }
        subDigit(kind, rods, i - 1, 1, out, Object.assign({}, ctx, { role: 'carry' }));
        step(out, kind, rods, i, 0, 10 - d, 'friend', here);
        return;
    }
    const l = r.l;
    if (c >= d) {
        if (d >= 5) step(out, kind, rods, i, -1, -(d - 5), 'direct', here);
        else if (l >= d) step(out, kind, rods, i, 0, -d, 'direct', here);
        else step(out, kind, rods, i, -1, 5 - d, 'brother', here);
        return;
    }
    subDigit(kind, rods, i - 1, 1, out, Object.assign({}, ctx, { role: 'carry' }));
    const k = 10 - d;
    if (k >= 5) step(out, kind, rods, i, 1, k - 5, 'friend', here);
    else if (l + k <= 4) step(out, kind, rods, i, 0, k, 'friend', here);
    else step(out, kind, rods, i, 1, -(5 - k), 'mix', here);
}

/**
 * Plan sign × n (a scaled integer) onto canonical rods. The operand's digits go in from
 * the highest place down — the way an abacus is worked, left to right.
 * Returns {steps, rods} — the rods after; throws PlanError('overflow'|'negative'|'canon').
 */
export function planOp(kind, rods, sign, n, op = 0) {
    if (!isCanonical(kind, rods)) throw new PlanError('canon');
    const work = rods.map((r) => ({ u: r.u, l: r.l }));
    const steps = [];
    const ds = String(Math.round(Math.abs(n))).split('').map(Number);
    if (ds.length > work.length) throw new PlanError(sign > 0 ? 'overflow' : 'negative');
    for (let k = 0; k < ds.length; k++) {
        const d = ds[k];
        if (!d) continue;
        const place = ds.length - 1 - k;
        const i = work.length - 1 - place;
        const ctx = { role: 'main', op, place };
        if (sign > 0) addDigit(kind, work, i, d, steps, ctx);
        else subDigit(kind, work, i, d, steps, ctx);
    }
    return { steps, rods: work };
}

/** Plan a chain [a, ±b, ±c…]: the first number is SET (from zero, direct), the rest added. */
export function planChain(kind, nRods, nums) {
    let rods = emptyRods(nRods);
    const groups = [];
    nums.forEach((v, idx) => {
        const sign = idx === 0 ? 1 : Math.sign(v) || 1;
        const r = planOp(kind, rods, sign, Math.abs(v), idx);
        rods = r.rods;
        groups.push({ op: idx, value: v, steps: r.steps });
    });
    return { groups, rods };
}

/** The formulas a chain of single steps uses: {direct, brother, friend, mix} → counts. */
export function formulasOf(steps) {
    const f = { direct: 0, brother: 0, friend: 0, mix: 0 };
    for (const s of steps) f[s.formula]++;
    return f;
}

/**
 * The formula of one digit move as shown to a person: '+4 = +5 − 1'.
 * kind is needed because the schoty have no brother.
 */
export function formulaText(formula, sign, d) {
    const p = sign > 0 ? '+' : '−', q = sign > 0 ? '−' : '+';
    if (formula === 'brother') return `${p}${d} = ${p}5 ${q} ${5 - d}`;
    if (formula === 'friend') return `${p}${d} = ${p}10 ${q} ${10 - d}`;
    if (formula === 'mix') {
        const k = 10 - d;
        return `${p}${d} = ${p}10 ${q} 5 ${p} ${5 - k}`;
    }
    return `${p}${d}`;
}

/* ═══════════════════════ reading what a person typed ═══════════════════════ */

/**
 * "25 + 38 − 17", "7+5=", "12,5 − 0,75" → {nums:[scaled…], dec} or {error}.
 * Numbers are scaled to the largest decimal count used; a leading minus is refused (an
 * abacus starts from a number on it). Multiplication and division are named, not solved.
 */
export function parseExpr(text, maxDec = 2) {
    const s = String(text || '')
        .replace(/[−‒–—]/g, '-')
        .replace(/[×xх*]/gi, '*')
        .replace(/[÷:]/g, '/')
        .replace(/\s+/g, '')
        .replace(/=+\??$/, '');
    if (!s) return { error: 'empty' };
    if (/[*/]/.test(s)) return { error: 'muldiv' };
    if (!/^[0-9.,+\-]+$/.test(s)) return { error: 'chars' };
    if (/^[+-]/.test(s)) return { error: s[0] === '-' ? 'neg' : 'chars' };
    const parts = s.match(/[+-]?[0-9]+(?:[.,][0-9]+)?/g);
    if (!parts || parts.join('') !== s) return { error: 'chars' };
    let dec = 0;
    for (const p of parts) { const m = p.match(/[.,]([0-9]+)$/); if (m) dec = Math.max(dec, m[1].length); }
    if (dec > maxDec) return { error: 'dec' };
    const nums = parts.map((p) => {
        const sign = p[0] === '-' ? -1 : 1;
        const [a, b = ''] = p.replace(/^[+-]/, '').split(/[.,]/);
        return sign * (Number(a) * Math.pow(10, dec) + Number((b + '000').slice(0, dec) || 0));
    });
    return { nums, dec };
}

/* ═══════════════════════ examples held to a set of formulas ═══════════════════════ */

/** Levels as the schools order them. Schoty have no brother: «без перехода» / «с переходом». */
export const LEVELS = {
    five: {
        simple: { allow: ['direct'], need: null },
        brother: { allow: ['direct', 'brother'], need: 'brother' },
        friend: { allow: ['direct', 'brother', 'friend'], need: 'friend' },
        mix: { allow: ['direct', 'brother', 'friend', 'mix'], need: 'mix' },
        all: { allow: ['direct', 'brother', 'friend', 'mix'], need: null },
    },
    ten: {
        simple: { allow: ['direct'], need: null },
        friend: { allow: ['direct', 'friend'], need: 'friend' },
        all: { allow: ['direct', 'friend'], need: null },
    },
};
export const levelsFor = (kind) => (KINDS[kind].five ? LEVELS.five : LEVELS.ten);

/** A tiny seeded generator, so a printed sheet and its answer key agree. */
export function rng(seed) {
    let a = (seed >>> 0) || 0x9e3779b9;
    return function () {
        a |= 0; a = (a + 0x6d2b79f5) | 0;
        let t = Math.imul(a ^ (a >>> 15), 1 | a);
        t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

const rint = (rand, lo, hi) => lo + Math.floor(rand() * (hi - lo + 1));
const randDigits = (rand, digits) => (digits <= 1 ? rint(rand, 1, 9) : rint(rand, Math.pow(10, digits - 1), Math.pow(10, digits) - 1));

function tryOp(kind, rods, sign, n, allow) {
    try {
        const r = planOp(kind, rods, sign, n);
        for (const s of r.steps) if (!allow.includes(s.formula)) return null;
        return r;
    } catch (e) { return null; }
}

/**
 * A chain of `count` numbers of `digits` digits under the level's formulas.
 * focus — optional {formula, d, sign}: at least one step must be exactly that move
 * (the schools drill one formula at a time: «+4 = +5 − 1»).
 * signs — 'pm' (both), 'plus', 'minus' (after the first number).
 * Returns {nums, answer, used} or null when the constraints leave nothing (rare).
 */
export function genChain({ kind, level = 'all', digits = 1, count = 5, focus = null, signs = 'pm', rods = null, rand = Math.random }) {
    const L = levelsFor(kind)[level] || levelsFor(kind).all;
    const n = rods || Math.max(digits + 2, 4);
    const allow = L.allow;
    const need = focus ? focus.formula : L.need;
    const hits = (steps) => steps.some((s) => s.formula === need && (!focus || (s.d === focus.d && s.sign === focus.sign && s.role === 'main')));
    for (let attempt = 0; attempt < 80; attempt++) {
        let rodsNow = emptyRods(n);
        const nums = [];
        const used = { direct: 0, brother: 0, friend: 0, mix: 0 };
        let met = !need;
        let total = 0;
        // the first number: set from zero (always direct)
        const first = randDigits(rand, digits);
        rodsNow = rodsOf(kind, digitsOf(first, n));
        total = first;
        nums.push(first);
        let ok = true;
        for (let k = 1; k < count; k++) {
            let best = null;
            for (let tr = 0; tr < 60; tr++) {
                let sign = signs === 'plus' ? 1 : signs === 'minus' ? -1 : (rand() < 0.5 ? -1 : 1);
                const v = randDigits(rand, digits);
                if (sign < 0 && v > total) { if (signs === 'minus') continue; sign = 1; }
                if (sign > 0 && total + v > capacity(n)) continue;
                const r = tryOp(kind, rodsNow, sign, v, allow);
                if (!r) continue;
                const cand = { sign, v, r, hit: need ? hits(r.steps) : false };
                // the focus formula is the point of the exercise: prefer a step that uses it
                if (!best || (cand.hit && !best.hit)) best = cand;
                if (!need || (cand.hit && rand() < 0.75) || (met && rand() < 0.5)) break;
            }
            if (!best) { ok = false; break; }
            nums.push(best.sign * best.v);
            rodsNow = best.r.rods;
            total += best.sign * best.v;
            for (const s of best.r.steps) used[s.formula]++;
            if (best.hit) met = true;
        }
        if (ok && met) return { nums, answer: total, used };
    }
    return null;
}

/** A × B or A ÷ B with whole answers (dA, dB — digits of the operands). */
export function genMulDiv({ op = 'mul', dA = 2, dB = 1, rand = Math.random }) {
    const a = randDigits(rand, dA), b = Math.max(2, randDigits(rand, dB));
    if (op === 'div') return { a: a * b, b, op, answer: a };
    return { a, b, op, answer: a * b };
}

/** A number of exactly `digits` digits (flash cards, «show the number»). */
export const genNumber = (digits, rand = Math.random) => (digits <= 1 ? rint(rand, 0, 9) : randDigits(rand, digits));

/* ═══════════════════════ the course ═══════════════════════
   Two courses: «five» for the soroban and the suanpan (the school order of mental
   arithmetic), «ten» for the Russian schoty. A lesson is a short text (translations,
   by id) and five tasks: {t:'set', n} — show n from zero; {t:'op', a, sign, b} — a is
   on the abacus, do ± b. Tasks are generated from the lesson's rule with a seed, so the
   same lesson gives the same tasks on a replay (and a different set with «ещё»). */

export const COURSE = {
    five: [
        { id: 'parts', rule: { t: 'set', from: [1, 3, 2, 4, 3] } },
        { id: 'n59', rule: { t: 'set', from: [5, 7, 6, 9, 8] } },
        { id: 'tens', rule: { t: 'set', digits: 2 } },
        { id: 'add_simple', rule: { t: 'op', level: 'simple', sign: 1 } },
        { id: 'sub_simple', rule: { t: 'op', level: 'simple', sign: -1 } },
        { id: 'add_brother', rule: { t: 'op', level: 'brother', sign: 1 } },
        { id: 'sub_brother', rule: { t: 'op', level: 'brother', sign: -1 } },
        { id: 'add_friend', rule: { t: 'op', level: 'friend', sign: 1 } },
        { id: 'sub_friend', rule: { t: 'op', level: 'friend', sign: -1 } },
        { id: 'mix', rule: { t: 'op', level: 'mix', sign: 0 } },
        { id: 'two_digit', rule: { t: 'op', level: 'all', sign: 0, digits: 2 } },
        { id: 'chain', rule: { t: 'chain', level: 'all', count: 4 } },
    ],
    ten: [
        { id: 'rows', rule: { t: 'set', from: [3, 1, 5, 2, 4] } },
        { id: 'to10', rule: { t: 'set', from: [6, 9, 7, 8, 10] } },
        { id: 'tens10', rule: { t: 'set', digits: 2 } },
        { id: 'add_simple10', rule: { t: 'op', level: 'simple', sign: 1 } },
        { id: 'sub_simple10', rule: { t: 'op', level: 'simple', sign: -1 } },
        { id: 'add_carry', rule: { t: 'op', level: 'friend', sign: 1 } },
        { id: 'sub_carry', rule: { t: 'op', level: 'friend', sign: -1 } },
        { id: 'big10', rule: { t: 'set', digits: 3 } },
        { id: 'two_digit10', rule: { t: 'op', level: 'all', sign: 0, digits: 2 } },
        { id: 'chain10', rule: { t: 'chain', level: 'all', count: 4 } },
    ],
};
export const courseFor = (kind) => (KINDS[kind].five ? COURSE.five : COURSE.ten);

/** The five tasks of a lesson. A «set» of 10 on the schoty is the full first wire. */
export function lessonTasks(kind, lesson, seed = 1, nRods = 7) {
    const rand = rng(seed * 7919 + lesson.id.length * 31);
    const R = lesson.rule;
    const out = [];
    for (let k = 0; k < 5; k++) {
        if (R.t === 'set') {
            if (R.from) out.push({ t: 'set', n: R.from[k % R.from.length] });
            else out.push({ t: 'set', n: randDigits(rand, R.digits || 1) });
            continue;
        }
        const sign = R.sign || (k % 2 ? -1 : 1);
        const g = genChain({ kind, level: R.level, digits: R.digits || 1, count: R.t === 'chain' ? (R.count || 4) : 2, signs: R.t === 'chain' ? 'pm' : (sign > 0 ? 'plus' : 'minus'), rods: nRods, rand });
        if (!g) { out.push({ t: 'set', n: randDigits(rand, 1) }); continue; }
        if (R.t === 'chain') out.push({ t: 'chain', nums: g.nums, answer: g.answer });
        else out.push({ t: 'op', a: g.nums[0], sign: Math.sign(g.nums[1]), b: Math.abs(g.nums[1]), answer: g.answer });
    }
    return out;
}

/** The place name key of a rod (for labels, sliders and the step text). */
export function placeKey(place) {
    return ['p0', 'p1', 'p2', 'p3', 'p4', 'p5', 'p6'][place] || 'pn';
}
